import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  Users, 
  QrCode, 
  Image, 
  Lock, 
  Zap, 
  Calendar, 
  Download, 
  Share2, 
  Shield, 
  Clock, 
  Smartphone,
  LayoutGrid,
  Infinity,
  Camera,
  Layers
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Upload,
      title: 'Instant Uploads',
      description: 'Real-time photo sharing during your events with automatic syncing across all devices.'
    },
    {
      icon: Users,
      title: 'Guest Collaboration',
      description: 'Let all attendees contribute their photos to create a complete collection of memories.'
    },
    {
      icon: QrCode,
      title: 'QR Code Sharing',
      description: 'Generate unique QR codes for each event for easy access and sharing.'
    },
    {
      icon: Image,
      title: 'High Quality Storage',
      description: 'Store and share photos in their original quality without compression.'
    },
    {
      icon: Lock,
      title: 'Private & Secure',
      description: 'Control access to your events with private galleries and secure sharing.'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized for speed with instant loading and real-time updates.'
    }
  ];

  const interfaceFeatures = [
    'Drag and drop uploads',
    'Real-time gallery updates',
    'Automatic photo organization',
    'Mobile-friendly interface'
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#fff9f9] to-[#fef4f4] z-0"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-6 rounded-full">
            POWERFUL FEATURES
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#FF4444] to-[#FF8080]">
            Everything you need for perfect event photos
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            Powerful features to make collecting and sharing event photos a breeze for you and your guests.
          </p>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-4">Core Features</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform is built with everything you need to make photo sharing simple and enjoyable.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#FF4444]" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our simple process makes it easy to create, collect, and share all your event photos.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">1. Create Event</h3>
              <p className="text-sm text-gray-600">
                Set up your event in seconds with a custom name, date, and privacy settings.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <QrCode className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">2. Share QR Code</h3>
              <p className="text-sm text-gray-600">
                Share your unique QR code with guests for easy access to the photo gallery.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Upload className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">3. Collect Photos</h3>
              <p className="text-sm text-gray-600">
                You and your guests can upload photos directly to the shared gallery.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Download className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">4. Download & Share</h3>
              <p className="text-sm text-gray-600">
                Download individual photos or the entire collection with just a few clicks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interface Section with Image */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-4 rounded-full">
                BEAUTIFUL INTERFACE
              </div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                Designed for everyone, not just tech enthusiasts
              </h2>
              <p className="text-gray-600 mb-8">
                Our intuitive design makes it easy for everyone to participate, whether they're tech-savvy or not. Just scan, upload, and share!
              </p>
              <ul className="space-y-3">
                {interfaceFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-[#FFEEEE] flex-shrink-0 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-[#FF4444]" />
                    </div>
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#FF4444]/5 to-[#FF8080]/5 rounded-xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6"
                  alt="Event photos"
                  className="w-full rounded-lg border border-gray-100 relative z-10"
                />
                <img
                  src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30"
                  alt="Event decoration"
                  className="absolute -bottom-4 -right-4 w-40 h-40 rounded-lg border-4 border-white object-cover shadow-sm z-20"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">More powerful features</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover all the ways our platform can enhance your photo collection experience.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Infinity className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">Unlimited Events</h3>
              <p className="text-sm text-gray-600">
                Create as many events as you need with our flexible credit-based system.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Smartphone className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">Mobile Optimized</h3>
              <p className="text-sm text-gray-600">
                Perfect experience on any device - upload and view photos on the go.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <LayoutGrid className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">Custom Galleries</h3>
              <p className="text-sm text-gray-600">
                Customize the look and feel of your galleries to match your event theme.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">Data Protection</h3>
              <p className="text-sm text-gray-600">
                Your photos are securely stored and protected with enterprise-grade security.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Camera className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">Photo Quality</h3>
              <p className="text-sm text-gray-600">
                Preserve the original quality of your photos without compression.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">Bulk Operations</h3>
              <p className="text-sm text-gray-600">
                Upload, download, and manage multiple photos at once for efficiency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF4444] to-[#FF6666] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to start collecting memories?</h2>
          <p className="mb-8 opacity-90">
            Join thousands of event hosts who trust us with their precious memories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-[#FF4444] px-8 py-3 rounded-lg font-medium hover:shadow-lg transition-all"
            >
              Get Started Free
            </Link>
            <Link
              to="/pricing"
              className="border border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-all"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Missing Check icon in the imports
const Check = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default Features;